// Player sign-in (spec/15 §2, P-02): a code to the phone (the default, while phone sign-in is on), or a
// password — through better-auth's own endpoints, in the contract (spec/08 §4.1); the session is its cookie,
// first-party on the portal's origin. Phone sign-in is the Platform's switch (`auth.phoneSignIn`, off since the
// owner's instruction of 2026-10-08): while `GET /v1/meta` does not say it is on, the page offers the password
// form alone — no method choice, no phone field. Passkeys join when the portal carries their flow.
//
// **Google, Discord and Steam** (F-IAM-03 … F-IAM-05), each only where `GET /v1/meta` says it is on (`social.tsx`).
// A button goes straight to the provider, with no consent: a provider account with no Clavian account comes back
// `?error=CONSENT_REQUIRED`, answered with "Create your Clavian account" (the terms, then the provider again). A verified address that already has an
// account comes back `account_not_linked`: sign in with the password, then link the provider from the account page.

import type { SocialProvider } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, RadioGroup } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../api/client';
import { queryKeys, usePhoneSignIn, useSocialProviders } from '../api/queries';
import { AuthLayout } from './AuthLayout';
import { useAuthFailure } from './auth-errors';
import { Failure, TextInput } from './form-controls';
import { SecondFactorForm, VerifyEmailForm } from './steps';
import { problemCode } from '../api/problems';
import { CreateAccountDialog, isSocialProvider, SocialButtons, SocialDivider, socialRefusal, withQuery, type SocialRoundTrip } from './social';

type Step =
  | { readonly kind: 'identify' }
  | { readonly kind: 'code'; readonly phoneNumber: string }
  // The password was right and two-factor is on: the authenticator's code next.
  | { readonly kind: 'second-factor' }
  // The password was right and the email was never verified: its code, then signed in with the same password.
  | { readonly kind: 'verify-email'; readonly email: string; readonly password: string };

/** E.164: what the contract's `PhoneNumber` accepts (spec/07 §2.1 "E.164 only"). */
const E164 = /^\+[1-9]\d{1,14}$/;
const compact = (phone: string) => phone.replace(/[\s()-]+/g, '');

export function SignInPage(): ReactNode {
  const { t } = useTranslation('player');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const api = useApi();
  const search = useSearch({ from: '/sign-in' });
  const phoneOn = usePhoneSignIn();
  const [chosen, setMethod] = useState<'phone' | 'password'>('phone');
  const method = phoneOn ? chosen : 'password';
  const [step, setStep] = useState<Step>({ kind: 'identify' });

  const signedIn = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
    await queryClient.refetchQueries({ queryKey: queryKeys.session });
    await navigate({ to: search.redirect ?? '/home' });
  };

  const { providers } = useSocialProviders();
  const back = search.redirect === undefined ? {} : { redirect: search.redirect };
  const returned = isSocialProvider(search.provider) ? search.provider : undefined;
  const [creating, setCreating] = useState<SocialProvider | null>(
    socialRefusal(search.error) === 'noAccount' && returned !== undefined ? returned : null,
  );
  const trip = (): SocialRoundTrip => ({
    landing: search.redirect ?? '/home',
    newUserLanding: withQuery('/welcome', back),
    onError: withQuery('/sign-in', back),
  });

  let body: ReactNode;
  if (step.kind === 'second-factor') {
    body = <SecondFactorForm onSignedIn={signedIn} onBack={() => setStep({ kind: 'identify' })} />;
  } else if (step.kind === 'verify-email') {
    body = (
      <VerifyEmailForm
        email={step.email}
        onVerified={async () => {
          const result = await api.signInEmail({ body: { email: step.email, password: step.password } });
          if (result.twoFactorRedirect === true) setStep({ kind: 'second-factor' });
          else await signedIn();
        }}
      />
    );
  } else if (step.kind === 'code' && phoneOn) {
    body = (
      <PhoneCodeForm
        phoneNumber={step.phoneNumber}
        onSignedIn={signedIn}
        onChangeNumber={() => setStep({ kind: 'identify' })}
      />
    );
  } else {
    body = (
      <>
        {phoneOn ? (
          <RadioGroup
            label={t('signIn.method.legend')}
            orientation="horizontal"
            value={method}
            onValueChange={(value) => setMethod(value === 'password' ? 'password' : 'phone')}
            options={[
              { value: 'phone', label: t('signIn.method.phone') },
              { value: 'password', label: t('signIn.method.password') },
            ]}
          />
        ) : null}
        {method === 'phone' ? (
          <PhoneNumberForm onSent={(phoneNumber) => setStep({ kind: 'code', phoneNumber })} />
        ) : (
          <PasswordForm
            onSignedIn={signedIn}
            onSecondFactor={() => setStep({ kind: 'second-factor' })}
            onVerifyEmail={(email, password) => setStep({ kind: 'verify-email', email, password })}
          />
        )}
        {providers.length === 0 ? null : (
          <>
            <SocialDivider />
            <SocialButtons providers={providers} trip={trip} />
          </>
        )}
        <p className="pw-auth__alt">
          <Link to="/sign-up" search={back}>
            {t('signIn.newHere')}
          </Link>
        </p>
      </>
    );
  }

  return (
    <AuthLayout
      title={step.kind === 'second-factor' ? t('signIn.secondFactor.title') : step.kind === 'verify-email' ? t('signIn.verify.title') : t('signIn.title')}
      subtitle={step.kind === 'identify' ? t(phoneOn ? 'signIn.intro' : 'signIn.introPassword') : undefined}
      footer={t('signUp.footer')}
    >
      {step.kind === 'identify' && creating === null ? (
        <SocialRefusalNotice error={search.error} provider={search.provider} redirect={search.redirect} />
      ) : null}
      {body}
      <CreateAccountDialog provider={creating} trip={trip} onClose={() => setCreating(null)} />
    </AuthLayout>
  );
}

/** What a refused Google, Discord or Steam round trip came back with (`?error=`), and the way forward. */
function SocialRefusalNotice({
  error,
  provider,
  redirect,
}: {
  readonly error: string | undefined;
  readonly provider: string | undefined;
  readonly redirect: string | undefined;
}): ReactNode {
  const { t } = useTranslation('player');
  const refusal = socialRefusal(error);
  if (refusal === undefined) return null;
  const message =
    refusal === 'banned'
      ? t('signIn.failure.banned')
      : isSocialProvider(provider)
        ? t(`social.error.${refusal}`, { provider: t(`social.provider.${provider}`) })
        : t(`signInError.error.${refusal}`);
  return (
    <div className="pw-alert pw-notice" role="alert">
      <p>{message}</p>
      {refusal === 'noAccount' ? (
        <p>
          <Link to="/sign-up" search={redirect === undefined ? {} : { redirect }}>
            {t('social.signUpInstead')}
          </Link>
        </p>
      ) : null}
    </div>
  );
}

function PhoneNumberForm({ onSent }: { readonly onSent: (phoneNumber: string) => void }) {
  const { t } = useTranslation('player');
  const api = useApi();
  const failureOf = useAuthFailure();
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ phoneNumber: string }>({ defaultValues: { phoneNumber: '' } });

  const submit = form.handleSubmit(async ({ phoneNumber }) => {
    setFailure(null);
    try {
      await api.sendPhoneNumberOTP({ body: { phoneNumber: compact(phoneNumber) } });
      onSent(compact(phoneNumber));
    } catch (error) {
      setFailure(failureOf(error));
    }
  });

  return (
    <form className="pw-form" onSubmit={(event) => void submit(event)} noValidate>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="phoneNumber"
        label={t('signIn.phone.number')}
        description={t('signIn.phone.numberHint')}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        autoFocus
        enterKeyHint="send"
        rules={{
          required: t('signIn.required'),
          validate: (value: string) => E164.test(compact(value)) || t('signIn.invalidPhone'),
        }}
      />
      <Button type="submit" variant="primary" block busy={form.formState.isSubmitting}>
        {t('signIn.phone.send')}
      </Button>
    </form>
  );
}

function PhoneCodeForm({
  phoneNumber,
  onSignedIn,
  onChangeNumber,
}: {
  readonly phoneNumber: string;
  readonly onSignedIn: () => Promise<void>;
  readonly onChangeNumber: () => void;
}) {
  const { t } = useTranslation('player');
  const api = useApi();
  const failureOf = useAuthFailure();
  const [failure, setFailure] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const form = useForm<{ code: string }>({ defaultValues: { code: '' } });

  const submit = form.handleSubmit(async ({ code }) => {
    setFailure(null);
    try {
      await api.verifyPhoneNumber({ body: { phoneNumber, code: code.trim() } });
      await onSignedIn();
    } catch (error) {
      setFailure(failureOf(error));
    }
  });

  const resend = async (): Promise<void> => {
    setFailure(null);
    setResent(false);
    setResending(true);
    try {
      await api.sendPhoneNumberOTP({ body: { phoneNumber } });
      setResent(true);
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setResending(false);
    }
  };

  return (
    <form className="pw-form" onSubmit={(event) => void submit(event)} noValidate>
      <p role="status">{resent ? t('signIn.phone.resent') : t('signIn.phone.sent', { phone: phoneNumber })}</p>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="code"
        label={t('signIn.phone.code')}
        description={t('signIn.phone.codeHint', { phone: phoneNumber })}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        maxLength={6}
        enterKeyHint="done"
        numeric
        rules={{ required: t('signIn.required') }}
      />
      <Button type="submit" variant="primary" block busy={form.formState.isSubmitting}>
        {t('signIn.phone.verify')}
      </Button>
      <div className="pw-form__actions">
        <Button variant="ghost" onClick={() => void resend()} busy={resending}>
          {t('signIn.phone.resend')}
        </Button>
        <Button variant="ghost" onClick={onChangeNumber}>
          {t('signIn.phone.change')}
        </Button>
      </div>
    </form>
  );
}

function PasswordForm({
  onSignedIn,
  onSecondFactor,
  onVerifyEmail,
}: {
  readonly onSignedIn: () => Promise<void>;
  readonly onSecondFactor: () => void;
  readonly onVerifyEmail: (email: string, password: string) => void;
}) {
  const { t } = useTranslation('player');
  const api = useApi();
  const failureOf = useAuthFailure();
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ identifier: string; password: string }>({ defaultValues: { identifier: '', password: '' } });

  const submit = form.handleSubmit(async ({ identifier, password }) => {
    setFailure(null);
    const trimmed = identifier.trim();
    try {
      const result = trimmed.includes('@')
        ? await api.signInEmail({ body: { email: trimmed, password } })
        : await api.signInUsername({ body: { username: trimmed, password } });
      if (result.twoFactorRedirect === true) onSecondFactor();
      else await onSignedIn();
    } catch (error) {
      // The right password for an address never verified: send its code and ask for it, then sign in.
      if (problemCode(error) === 'EMAIL_NOT_VERIFIED' && trimmed.includes('@')) {
        await api.sendEmailVerificationOTP({ body: { email: trimmed, type: 'email-verification' } }).catch(() => undefined);
        onVerifyEmail(trimmed, password);
        return;
      }
      setFailure(failureOf(error));
    }
  });

  return (
    <form className="pw-form" onSubmit={(event) => void submit(event)} noValidate>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="identifier"
        label={t('signIn.password.identifier')}
        autoComplete="username"
        autoFocus
        enterKeyHint="next"
        rules={{ required: t('signIn.required') }}
      />
      <TextInput
        control={form.control}
        name="password"
        label={t('signIn.password.password')}
        type="password"
        autoComplete="current-password"
        enterKeyHint="go"
        rules={{ required: t('signIn.required') }}
      />
      <p className="pw-auth__forgot">
        <Link to="/forgot-password" search={{}}>
          {t('signIn.password.forgot')}
        </Link>
      </p>
      <Button type="submit" variant="primary" block busy={form.formState.isSubmitting}>
        {t('signIn.password.submit')}
      </Button>
    </form>
  );
}
